<template>
  <div class="copy-candidate-container">
    <DialogWrapper
      closeOnCancel
      ref="copyCandidateDialog"
      class="copy-candidate-dialog"
      saveBtnLabelI18nKey="common.btn.copy"
      :show.sync="show"
      :loading="loading"
      :title="$t(`${i18nPrefix}.title.${copyCandidateTitleKey}`)"
      @save="handleSaveClick"
    >
      <div class="content">
        <div class="job-selector-container">
          <el-select
            v-model="job"
            :placeholder="$t(`${i18nPrefix}.placeholder.selectJob`)"
            filterable
            ref="jobSelector"
            class="job-selector"
            value-key="id"
            :filter-method="handleJobListSearch"
            @visible-change="onJobSelectorVisibleChange"
          >
            <el-option
              v-for="item in renderJobList"
              :key="item.id"
              :style="{'width':optionWidth}"
              :label="`${item.id} ${item.title} ${formatApplicantCount(item.number)}`"
              :value="item"
            ></el-option>
          </el-select>
          <el-button class="add-job-btn" type="text" @click="handleAddJobBtnClick">
            <i class="icomoon-add-box"></i>
          </el-button>
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
        </div>
        <div class="job-selected-list">
          <div
            v-for="(item, index) in jobSelectedList"
            class="select-content"
            :key="index"
          >
            <div class="job-name">{{ `${item.title} ${formatApplicantCount(item.number, true)}` }}</div>
            <el-button class="remove-job-btn icomoon-remove-box" type="text" @click="handleRemoveJobBtnClick(item)"></el-button>
          </div>
        </div>
      </div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { getI18nPropertyValue } from '@/i18n'
import DialogWrapper from '../../../../components/dialog/dialogWrapper.vue'

export default {
  components: {
    DialogWrapper
  },
  props: {
    applicationList: {
      required: false
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    candidateList: {
      required: false
    },
    isAllApplication: {
      required: false
    },
    searchParams: {
      required: false
    },
    jobId: {
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      getI18nPropertyValue,
      show: false,
      loading: false,
      optionWidth: 0,
      errorMessage: null,
      job: null,
      jobList: [],
      searchedJobList: [],
      renderJobList: [],
      jobSelectedList: [],
      jobLoader: {
        loaded: false,
        loading: false
      },
      page: {
        current: 1,
        size: 20,
        get startIndex () {
          return (this.current - 1) * this.size
        },
        get endIndex () {
          return this.current * this.size
        }
      }
    }
  },
  computed: {
    copyCandidateTitleKey () {
      const candidates = this.isTalentPool ? this.candidateList : this.applicationList
      return !this.isAllApplication && (candidates || []).length === 1
        ? 'copyCandidate'
        : 'copyCandidates'
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    }
  },
  created () {

  },
  methods: {
    ...mapActions('job', ['getTargetJobList']),
    ...mapActions('application', ['copyCandidate']),
    ...mapActions('talent', ['copyTalentCandidate']),
    ...mapActions('agency', ['getCopyCandidateJobList']),
    getApplicantNounKey (count) {
      return Number(count) === 1 ? 'applicant' : 'applicants'
    },
    formatApplicantCount (count, compact = false) {
      if (this.$i18n.locale !== 'en') {
        const noun = this.$t(`${this.i18nPrefix}.label.people`)
        return compact ? `( ${count}${noun} )` : `( ${count} ${noun} )`
      }
      const noun = this.$t(`${this.i18nPrefix}.label.${this.getApplicantNounKey(count)}`)
      return `(${count} ${noun})`
    },
    showDialog () {
      this.show = true
    },
    async getJobList () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let resp = null
        if (this.isAgency) {
          const params = {
            candidate_ids: _.map(this.applicationList, item => item.candidate.id).join(',')
          }
          resp = await this.getCopyCandidateJobList(params)
        } else {
          let candidateList = null
          if (this.isTalentPool) {
            candidateList = this.candidateList
          } else {
            candidateList = _.map(this.applicationList, item => item.candidate.id)
          }
          const params = {
            candidateList: candidateList
          }
          resp = await this.getTargetJobList(params)
        }
        if (resp) {
          this.jobList = resp
        }
      } finally {
        this.loading = false
      }
    },
    handleJobListSearch (keywords) {
      if (keywords) {
        keywords = keywords.toUpperCase()
        this.searchedJobList = _.filter(this.jobList, job => job.title.toUpperCase().includes(keywords))
      } else {
        this.searchedJobList = this.jobList
      }
      const page = this.page
      page.current = 1
      const pageData = _.slice(this.searchedJobList, page.startIndex, page.endIndex)
      this.renderJobList = pageData
    },
    onJobSelectorVisibleChange (visible) {
      const page = this.page
      if (visible) {
        const scrollEl = this.$refs.jobSelector.$refs.scrollbar.$el.firstElementChild
        if (!scrollEl.onscroll) {
          scrollEl.onscroll = () => {
            const distance = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight - 10
            if (distance <= 20) {
              page.current += 1
              const pageData = _.slice(this.searchedJobList, page.startIndex, page.endIndex)
              this.renderJobList.push(...pageData)
            }
          }
        }
        const width = this.$refs.jobSelector.$el.getBoundingClientRect().width
        if (width) {
          this.optionWidth = `${width}px`
        }
        this.handleJobListSearch()
      } else {
        this.renderJobList = []
      }
    },
    handleAddJobBtnClick () {
      if (!this.job) {
        this.errorMessage = this.$t(`${this.i18nPrefix}.message.addJobRequired`)
        return
      }
      this.jobSelectedList.push(this.job)
      this.jobList = _.filter(this.jobList, item => item.id !== this.job.id)
      this.job = null
    },
    handleRemoveJobBtnClick (job) {
      this.jobSelectedList = _.filter(this.jobSelectedList, item => item.id !== job.id)
      this.jobList.push(job)
    },
    handleSaveClick () {
      const isValid = this.jobSelectedList.length > 0 || this.job
      if (!isValid) {
        this.errorMessage = this.$t(`${this.i18nPrefix}.message.addJobRequired`)
        return
      }
      if (this.jobSelectedList.length < 1) {
        this.handleAddJobBtnClick()
      }
      this.handleCopyCandidate()
    },
    async handleCopyCandidate () {
      if (this.loading) {
        return
      }
      this.errorMessage = null
      const params = {
        job_id: this.jobId,
        search_params: this.searchParams,
        is_all_application: this.isAllApplication,
        target_job_ids: _.map(this.jobSelectedList, item => item.id),
        candidate_list: []
      }
      if (this.isTalentPool) {
        params.candidate_list = this.candidateList
      } else {
        params.candidate_list = _.map(this.applicationList, item => {
          return {
            job_id: item.job.id,
            candidate_id: item.candidate.id
          }
        })
      }
      this.loading = true
      let resp = null
      try {
        if (this.isTalentPool) {
          resp = await this.copyTalentCandidate(params)
        } else {
          resp = await this.copyCandidate(params)
        }
        const statusCount = _.countBy(resp, 'status')
        if (statusCount.success && statusCount.success > 0) {
          if (statusCount.failed && statusCount.failed > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.copyCandidate.notAllSuccess`, { count: statusCount.failed }))
          } else {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.copyCandidate.success`))
          }
          this.show = false
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.copyCandidate.failed`))
        }
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    show () {
      if (this.show) {
        this.job = null
        this.jobSelectedList = []
        this.getJobList()
      }
      this.errorMessage = null
    },
    job () {
      const isClearError = this.job || this.jobSelectedList.length > 0
      if (isClearError) {
        this.errorMessage = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.copy-candidate-container {
  .copy-candidate-dialog {
    .content {
      .job-selector-container {
        position: relative;
        display: flex;
        align-items: center;
        .job-selector {
          margin-right: 50px;
        }

        .add-job-btn {
          margin-left: auto;
          padding: 0;
          font-size: 30px;

          .icomoon-add-box::before {
            color: #0096CC;
          }
        }

        .error-message {
          position: absolute;
          top: 100%;
          left: 0;
          color: #f56c6c;
          font-size: 12px;
          line-height: 1;
          padding-top: 4px;
        }
      }

      .job-selected-list {
        margin-top: 20px;

        .select-content {
          .job-name {
            display: inline-block;
            width: 70%;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          .remove-job-btn {
            display: inline-block;
            width: 30%;
            vertical-align: middle;
            text-align: right;
            font-size: 30px;
            padding: 0;

            &::before {
              color: #0096CC;
            }
          }
        }
      }
    }
  }
}
</style>
